import {
  PresentationChart,
  Lightbulb,
  BookBookmark,
  Scales,
  CalendarStar,
  Sliders,
  UsersThree,
  MagnifyingGlass,
  ListChecks,
  IdentificationCard,
  Briefcase,
  ArrowsLeftRight,
  type Icon,
} from "@phosphor-icons/react";

export interface Capability {
  icon: Icon;
  title: string;
  description: string;
}

export type StrategyStatus = "available" | "coming-soon" | "unavailable";

/** One strategy tile: a label/value pair per row, e.g. ["Deals", "None"]. */
export interface StrategyCoverage {
  name: string;
  status: StrategyStatus;
  rows: [label: string, value: string][];
  /** Supported strategies take a double-width tile. */
  isWide?: boolean;
}

/**
 * Everything the Welcome modal says about one DealOS feature. The overview
 * card, the feature's own slide and the mobile card all read from here.
 */
export interface WelcomeFeature {
  id: string;
  title: string;
  icon: Icon;
  summary: string;
  slideIntro: string;
  mobileBullets: string[];
  /** Five cards: two beside the intro, three in the row below. */
  capabilities: Capability[];
  strategyHeading: string;
  strategies: StrategyCoverage[];
}

const OTHER_STRATEGIES = ["Liquid Credit", "Infrastructure", "Secondaries"];

const STATUS_TEXT: Record<Exclude<StrategyStatus, "available">, string> = {
  "coming-soon": "Coming soon",
  unavailable: "None",
};

function notAvailable(
  names: string[],
  labels: string[],
  status: Exclude<StrategyStatus, "available">
): StrategyCoverage[] {
  return names.map((name) => ({
    name,
    status,
    rows: labels.map((label): [string, string] => [label, STATUS_TEXT[status]]),
  }));
}

function namesWith(feature: WelcomeFeature, status: StrategyStatus): string {
  const names = feature.strategies.filter((s) => s.status === status).map((s) => s.name);
  return names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names.at(-1)}` : names.join("");
}

/** One-line coverage for views without the strategy tiles, e.g. the mobile card. */
export function coverageSummary(feature: WelcomeFeature): string {
  const available = `Available for ${namesWith(feature, "available")}.`;
  const comingSoon = namesWith(feature, "coming-soon");
  return comingSoon ? `${available} ${comingSoon} coming soon.` : available;
}

const PAST_DEALS_SUMMARY =
  "Search and interrogate CVC's past PE and Private Credit deals, their IC papers and PE DD docs.";

export const PAST_DEALS_FEATURE: WelcomeFeature = {
  id: "past-deals",
  title: "Find and Q&A Past Deals",
  icon: PresentationChart,
  summary: PAST_DEALS_SUMMARY,
  slideIntro: PAST_DEALS_SUMMARY,
  mobileBullets: [
    "Learn from CVC experience across 1000s of IC memos and PE CDDs",
    "Benchmark valuations, multiples and deal structures",
    "Find past IC questions and how memos handled them",
    "Pull credentials for marketing materials and offer letters",
  ],
  capabilities: [
    {
      icon: Lightbulb,
      title: "Learn from CVC experience",
      description: "Leverage 1000s of IC memos and PE CDDs to get up to speed on a new area.",
    },
    {
      icon: BookBookmark,
      title: "Prepare for IC",
      description: "Find past IC questions and see how memos handled them.",
    },
    {
      icon: Scales,
      title: "Benchmark your deal against CVC history",
      description:
        "Compare valuations, multiples, and deal structures across CVC's portfolio history.",
    },
    {
      icon: CalendarStar,
      title: "Pull credentials for marketing materials",
      description: "Quickly assemble track record for founder-facing decks / offer letters.",
    },
    {
      icon: Sliders,
      title: "Learn from execution and value creation",
      description: "Understand how VCP levers were applied and what worked.",
    },
  ],
  strategyHeading: "Data available by strategy:",
  strategies: [
    {
      name: "Private Equity",
      status: "available",
      rows: [
        ["Deals", "All. Reached PIR after 2011 (some masked)."],
        ["Docs", "Published IC papers / CDDs."],
      ],
      isWide: true,
    },
    {
      name: "Private Credit",
      status: "available",
      rows: [
        ["Deals", "Deals whose documents you can access."],
        ["Docs", "Published IC papers."],
      ],
      isWide: true,
    },
    ...notAvailable(OTHER_STRATEGIES, ["Deals", "Docs"], "unavailable"),
  ],
};

const EXPERT_FINDER_SUMMARY =
  "Find the CVC colleagues with the deepest deal experience in any sector, theme or geography.";

export const EXPERT_FINDER_FEATURE: WelcomeFeature = {
  id: "expert-finder",
  title: "Expert Finder",
  icon: UsersThree,
  summary: EXPERT_FINDER_SUMMARY,
  slideIntro:
    "Find the CVC colleagues with the deepest deal experience in any sector, theme or geography, before you commission an external expert call.",
  mobileBullets: [
    "Get a ranked list of internal experts for any topic",
    "See the deals, roles and recency behind each ranking",
    "Pull a colleague's full deal history and sector mix",
    "Check internal expertise before commissioning an external call",
  ],
  capabilities: [
    {
      icon: MagnifyingGlass,
      title: "Find who knows a space",
      description:
        "Ask in plain language, e.g. carve-outs in European industrials, and get a ranked list of colleagues.",
    },
    {
      icon: ListChecks,
      title: "See why they rank",
      description: "Every ranking is backed by the specific deals, roles and recency that justify it.",
    },
    {
      icon: IdentificationCard,
      title: "Drill into a colleague",
      description: "Pull anyone's full CVC deal history with their sector, country and role mix.",
    },
    {
      icon: Briefcase,
      title: "Find a past deal team",
      description: "See who worked on a specific CVC deal and what else they have done.",
    },
    {
      icon: ArrowsLeftRight,
      title: "Compare a shortlist",
      description: "Compare named colleagues side by side on their CVC deal experience.",
    },
  ],
  strategyHeading: "Expertise available by strategy:",
  strategies: [
    {
      name: "Private Equity",
      status: "available",
      rows: [
        ["People", "CVC deal team members."],
        ["Based on", "Deal team roles and deal content."],
      ],
      isWide: true,
    },
    ...notAvailable(["Private Credit"], ["People", "Based on"], "coming-soon"),
    ...notAvailable(OTHER_STRATEGIES, ["People", "Based on"], "unavailable"),
  ],
};

/** Feature order drives the overview cards, the slide order and the mobile list. */
export const WELCOME_FEATURES: WelcomeFeature[] = [PAST_DEALS_FEATURE, EXPERT_FINDER_FEATURE];
